import * as React from 'react';

import {
  enumTrailerSortOption,
  enumTrailerStatus,
  TrailerStatus,
} from '@rematter/frontend-lib-api-client';
import { Button } from '@rematter/ui-web';
import {
  DataGrid,
  DataGridColumn,
  HeaderIcons,
  Pagination,
  type PaginationProps,
  Badge,
  BadgeProps,
} from '@rematter/ui-web';
import {
  Content,
  Footer,
  Toolbar,
  FloatingToolbar,
  Separator,
} from '@rematter/ui-web';
import { SearchField } from '@rematter/ui-web';
import { Loading } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';
import { PlusCircle, Trash } from 'lucide-react';

import { TrailerDialog } from './TrailerDialog';

import { useTranslations } from '@/transportation/i18n/useTranslations';
import {
  $limit,
  $offset,
  $searchTerm,
} from '~/modules/transportation/_state/trailersList.state';
import { useDeleteTrailerMutation } from '~/modules/transportation/hooks/mutations/useDeleteTrailerMutation';
import { useTrailersQuery } from '~/modules/transportation/hooks/queries/useTrailersQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { TrailerRecord } from '~/modules/transportation/types';
import useDebouncedValue from '~/shared/hooks/useDebounce';

const getStatusColor = (status: TrailerStatus): BadgeProps['color'] => {
  switch (status) {
    case enumTrailerStatus.Available:
      return 'success';
    case enumTrailerStatus.InUse:
      return 'purple';
    case enumTrailerStatus.Inactive:
      return 'failure';
    default:
      return 'neutral';
  }
};

export const TrailersListView = () => {
  const toast = useToast();
  const t = useTranslations();
  const [selectedRows, setSelectedRows] = React.useState<TrailerRecord[]>([]);
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editTrailer, setEditTrailer] = React.useState<TrailerRecord | null>(
    null,
  );

  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const { data, error, isLoading, refetch } = useTrailersQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit,
    offset,
    sorting: [enumTrailerSortOption.CreatedAtDesc],
  });

  const { mutateAsync: deleteTrailer } = useDeleteTrailerMutation();

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  };

  const handleCreate = () => {
    setEditTrailer(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await Promise.all(
        selectedRows.map((row) => deleteTrailer({ id: row.id })),
      );
      toast.success(t.trailersView.messages.deleteSuccess(selectedRows.length));
      setSelectedRows([]);
      refetch();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.trailersView.messages.error,
      );
    }
  };

  const COLUMNS: DataGridColumn<TrailerRecord>[] = [
    {
      key: 'status',
      header: t.trailersView.table.columns.status,
      icon: HeaderIcons.Tag,
      width: 150,
      resizable: true,
      sortable: true,
      cell: ({ row }) => (
        <Badge color={getStatusColor(row.status)} size="small">
          {t.trailersView.status[row.status]}
        </Badge>
      ),
    },
    {
      key: 'licensePlate',
      header: t.trailersView.table.columns.licensePlate,
      icon: HeaderIcons.Text,
      width: 150,
      resizable: true,
      cell: ({ row }) => row.licensePlate,
    },
    {
      key: 'transportationType',
      header: t.trailersView.table.columns.transportationType,
      icon: HeaderIcons.Tags,
      width: 200,
      resizable: true,
      sortable: true,
      cell: ({ row }) => row.transportationType.name,
    },
    {
      key: 'capacity',
      header: t.trailersView.table.columns.capacity,
      icon: HeaderIcons.Weight,
      width: 150,
      resizable: true,
      sortable: true,
      cell: ({ row }) => `${row.capacity} lb`,
    },
    {
      key: 'location',
      header: t.trailersView.table.columns.location,
      icon: HeaderIcons.Address,
      grow: 1,
      resizable: true,
      cell: ({ row }) =>
        row.location
          ? `${row.location.latitude}, ${row.location.longitude}`
          : '-',
    },
  ];

  if (error) {
    return (
      <TransportationRootLayout>
        <div className="p-4 text-failure">
          {t.trailersView.content.error(error.message)}
        </div>
      </TransportationRootLayout>
    );
  }

  const trailers: TrailerRecord[] = data?.trailers ?? [];

  return (
    <TransportationRootLayout className="@TrailersListView">
      <Toolbar>
        <div className="flex items-center gap-4">
          <SearchField
            value={searchTerm ?? ''}
            onChange={handleSearch}
            placeholder={t.trailersView.toolbar.search}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            onPress={handleCreate}
            variant="solid"
            color="primary"
            icon={<PlusCircle />}
          >
            {t.trailersView.toolbar.create}
          </Button>
        </div>
      </Toolbar>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <span className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>{' '}
          selected
        </span>
        <Separator hideDivider />
        <Button
          variant="solid"
          color="failure"
          icon={<Trash />}
          isDisabled={selectedRows.length < 1}
          onPress={handleDelete}
        >
          {t.trailersView.toolbar.delete(selectedRows.length)}
        </Button>
      </FloatingToolbar>

      <Content>
        <Loading isLoading={isLoading}>
          <DataGrid<TrailerRecord>
            columns={COLUMNS}
            dataSource={trailers}
            rowKey="id"
            aria-label={t.trailersView.table.aria}
            selectionMode="multiple"
            onSelectionChange={(keys) => {
              if (keys === 'all') {
                setSelectedRows(trailers);
              } else {
                const selectedTrailers = trailers.filter((trailer) =>
                  keys.has(String(trailer.id)),
                );
                setSelectedRows(selectedTrailers);
              }
            }}
            className="w-full text-sm"
            fullWidth
          />
        </Loading>
      </Content>

      <Footer>
        <Pagination
          limit={limit}
          offset={offset}
          total={data?.total ?? 0}
          onChange={handlePaginationChange}
        />
      </Footer>

      <TrailerDialog
        trailer={
          editTrailer
            ? {
                id: editTrailer.id,
                status: editTrailer.status,
                licensePlate: editTrailer.licensePlate,
                transportationTypeId: editTrailer.transportationType.id,
                capacity: editTrailer.capacity,
                country: editTrailer.country,
                state: editTrailer.state,
                facilityId: editTrailer.facilityId,
              }
            : undefined
        }
        isOpen={isDialogOpen}
        onClose={() => {
          setEditTrailer(null);
          setIsDialogOpen(false);
        }}
        onSuccess={() => {
          setEditTrailer(null);
          setIsDialogOpen(false);
          refetch();
        }}
      />
    </TransportationRootLayout>
  );
};
